/**
 * workspaceService.ts
 *
 * Integração com o backend (Auth + Workspace).
 * - Lista workspaces do usuário via /api/workspaces
 * - Cria workspace via /api/workspaces
 * - Workspace ativo permanece local (localStorage)
 */

import { environmentStorageKey } from "@/lib/appEnvironment"
import { http } from "@/lib/http"
import { getAccessToken, uploadMultipart } from "@/services/blobService"
import { API_BASE_URL } from "@/lib/apiConfig"
import { ApiError } from "@/types/api"
import type {
  WorkspaceSummary,
  WorkspaceDetail,
  WorkspaceMemberDto,
  WorkspaceMemberAdminDto,
  WorkspaceMemberListDto,
  UserGroupDto,
  InvitationMetadata,
  MyInvitation,
  CreateWorkspacePayload,
  UpdateWorkspacePayload,
  AddMemberPayload,
  UpdateMemberRolePayload,
  UserSearchResult,
  InviteMembersPayload,
  BulkInvitationResult,
  ChangeMemberRoleAdminPayload,
  AdminPermissionResponse,
  WorkspaceLogoUploadResponse,
} from "@/types/workspace"
import type { MeResponse } from "@/types/auth"
import { getBrowserTimeZone } from "@/lib/timezone"

const WS_ACTIVE_KEY = environmentStorageKey("dkro.workspace.active")

type CoreWorkspaceDto = {
  id: string
  name?: string
  nameDisplay?: string
  Name?: string
  logoUrl?: string | null
  logoUrlAvatar?: string | null
  LogoUrl?: string | null
  slug?: string | null
  memberCount?: number
  createdAt?: string
  role?: string
  Role?: string
  timeZone?: string | null
  TimeZone?: string | null
}

type CoreWorkspaceMemberDto = {
  id: string
  userId: string
  email: string
  fullName: string
  avatarUrl?: string | null
  role: string
  joinedAt?: string
  createdAt?: string
}

type CoreAdminMemberDto = {
  id: string
  userId?: string | null
  email: string
  fullName: string
  avatarUrl?: string | null
  role: string
  status: string
  createdAt: string
  costCenterNames: string[]
  usingWorkspaceDefaultCostCenter: boolean
}

type CorePermissionDto = {
  module: number | string
  actions?: string[]
  permissions?: number[]
}

const actionToPermission: Record<string, number> = {
  create: 1,
  alter: 2,
  update: 2,
  edit: 2,
  remove: 3,
  delete: 3,
}

const moduleToNumber: Record<string, number> = {
  members: 0,
  usergroups: 1,
  costcenters: 2,
  resourcegroups: 3,
  forms: 4,
  workflows: 5,
  reports: 6,
  tasks: 7,
}

// ─── helpers ────────────────────────────────────────────────────────────────

const read = <T>(key: string, fallback: T): T => {
  if (typeof window === "undefined") return fallback
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

const write = (key: string, value: unknown) => {
  if (typeof window === "undefined") return
  window.localStorage.setItem(key, JSON.stringify(value))
}

function normalizeWorkspace(ws: CoreWorkspaceDto): WorkspaceSummary {
  const nameDisplay = ws.name ?? ws.nameDisplay ?? ws.Name ?? "Workspace sem nome"

  return {
    id: ws.id,
    nameDisplay,
    logoUrlAvatar: ws.logoUrl ?? ws.logoUrlAvatar ?? ws.LogoUrl ?? null,
    role: ws.role ?? ws.Role,
    timeZone: ws.timeZone ?? ws.TimeZone ?? null,
  }
}

function normalizeMember(member: CoreWorkspaceMemberDto): WorkspaceMemberDto {
  return {
    userId: member.userId,
    fullName: member.fullName,
    email: member.email,
    photoUrl: member.avatarUrl ?? null,
    roleName: member.role,
    joinedAt: member.joinedAt ?? member.createdAt ?? "",
  }
}

function normalizeAdminMember(member: CoreAdminMemberDto): WorkspaceMemberAdminDto {
  return {
    memberId: member.id,
    userId: member.userId ?? null,
    fullName: member.fullName,
    email: member.email,
    photoUrl: member.avatarUrl ?? null,
    roleName: member.role,
    status: member.status,
    joinedAt: member.createdAt,
    costCenterNames: member.costCenterNames,
    usingWorkspaceDefaultCostCenter: member.usingWorkspaceDefaultCostCenter,
  }
}

function normalizePermission(permission: CorePermissionDto): AdminPermissionResponse[] {
  const moduleValue = typeof permission.module === "number"
    ? permission.module
    : moduleToNumber[permission.module.toLowerCase()] ?? Number(permission.module)
  const numericPermissions = permission.permissions ?? permission.actions?.map((action) => actionToPermission[action.toLowerCase()]).filter(Boolean) ?? []

  return numericPermissions.map((item) => ({
    userId: "",
    module: moduleValue,
    permission: item,
  }))
}

// ─── API ────────────────────────────────────────────────────────────────────

/** Lista workspaces do usuário */
export async function getWorkspaces(): Promise<WorkspaceSummary[]> {
  const [workspaces, me] = await Promise.all([
    http.get<CoreWorkspaceDto[]>("/api/workspaces"),
    http.get<MeResponse>("/api/auth/me"),
  ])
  const rolesByWorkspace = new Map((me.workspaces ?? []).map((ws) => [ws.id, ws.role]))

  return workspaces.map((workspace) => {
    const normalized = normalizeWorkspace(workspace)
    return {
      ...normalized,
      role: rolesByWorkspace.get(workspace.id) ?? normalized.role,
    }
  })
}

/** Cria um novo workspace no backend */
export async function createWorkspace(payload: CreateWorkspacePayload): Promise<WorkspaceSummary> {
  const created = await http.post<CoreWorkspaceDto>("/api/workspaces", {
    name: payload.nameDisplay,
    timeZone: payload.timeZone ?? getBrowserTimeZone(),
  })

  return {
    ...normalizeWorkspace(created),
    role: "Owner",
  }
}

/** Define o workspace ativo */
export function setActiveWorkspace(id: string): void {
  write(WS_ACTIVE_KEY, id)
}

/** Retorna o id do workspace ativo */
export function getActiveWorkspaceId(): string | null {
  return read<string | null>(WS_ACTIVE_KEY, null)
}

/** Retorna o workspace ativo completo (ou undefined) */
export async function getActiveWorkspace(): Promise<WorkspaceSummary | undefined> {
  const id = getActiveWorkspaceId()
  if (!id) return undefined
  const list = await getWorkspaces()
  return list.find((w) => w.id === id)
}

/** Remove todos os dados locais de workspace (ex.: logout) */
export function clearWorkspaces(): void {
  if (typeof window === "undefined") return
  window.localStorage.removeItem(WS_ACTIVE_KEY)
}

// ─── Workspace management ─────────────────────────────────────────────────────

/** Retorna detalhes completos de um workspace com membros */
export async function getWorkspaceDetail(id: string): Promise<WorkspaceDetail> {
  const [workspace, members] = await Promise.all([
    http.get<CoreWorkspaceDto>(`/api/workspaces/${id}`),
    http.get<CoreWorkspaceMemberDto[]>(`/api/workspaces/${id}/members`),
  ])

  return {
    ...normalizeWorkspace(workspace),
    isActive: true,
    createdAt: workspace.createdAt ?? "",
    members: members.map(normalizeMember),
  }
}

/** Atualiza nome e logo do workspace */
export async function updateWorkspace(id: string, payload: UpdateWorkspacePayload): Promise<WorkspaceDetail> {
  const updated = await http.put<CoreWorkspaceDto>(`/api/workspaces/${id}`, {
    name: payload.nameDisplay,
    logoUrl: payload.logoUrlAvatar ?? null,
    slug: undefined,
    timeZone: payload.timeZone ?? undefined,
  })

  return {
    ...normalizeWorkspace(updated),
    isActive: true,
    createdAt: updated.createdAt ?? "",
    members: [],
  }
}

/** Upload do logo do workspace — retorna a URL final já commitada */
export async function uploadWorkspaceLogo(id: string, file: File): Promise<WorkspaceLogoUploadResponse> {
  const logoUrl = await uploadMultipart<string>(`/api/workspaces/${id}/logo`, file)

  return {
    temporaryLogoFileName: "",
    logoUrlAvatar: logoUrl ?? "",
  }
}

/** Adiciona membro ao workspace */
export async function addMember(workspaceId: string, payload: AddMemberPayload): Promise<WorkspaceMemberDto> {
  const member = await http.post<CoreWorkspaceMemberDto>(`/api/workspaces/${workspaceId}/members`, {
    userId: payload.userId,
    role: payload.roleName,
  })

  return normalizeMember(member)
}

/** Atualiza a role de um membro */
export async function updateMemberRole(
  workspaceId: string,
  userId: string,
  payload: UpdateMemberRolePayload,
): Promise<WorkspaceMemberDto> {
  const member = await http.put<CoreWorkspaceMemberDto>(`/api/workspaces/${workspaceId}/members/${userId}/role`, {
    role: payload.roleName,
  })

  return normalizeMember(member)
}

/** Remove um membro do workspace */
export async function removeMember(workspaceId: string, userId: string): Promise<void> {
  await http.delete(`/api/workspaces/${workspaceId}/members/${userId}`)
}

/** Busca um usuário por e-mail (para adicionar como membro) */
export async function searchUserByEmail(email: string): Promise<UserSearchResult> {
  // POST em vez de GET: evita que o e-mail pesquisado fique exposto na URL.
  return http.post<UserSearchResult>("/api/users/search", { email })
}

/** Lista membros ativos do workspace (plano operacional / Tasks). */
export async function listWorkspaceMembers(workspaceId: string): Promise<WorkspaceMemberListDto[]> {
  return http.get<WorkspaceMemberListDto[]>(`/api/workspaces/${workspaceId}/members`)
}

/** Lista grupos de usuários do workspace. */
export async function listUserGroups(workspaceId: string): Promise<UserGroupDto[]> {
  return http.get<UserGroupDto[]>(`/api/workspaces/${workspaceId}/groups`)
}

// ─── Invitations ──────────────────────────────────────────────────────────────

/** Aceita um convite de workspace (requer autenticação) */
export async function acceptInvitation(token: string): Promise<void> {
  await http.post("/api/workspaces/invitations/accept", { token })
}

/** Lista convites do usuário autenticado, incluindo o histórico de respostas. */
export async function getMyInvitations(): Promise<MyInvitation[]> {
  return http.get<MyInvitation[]>("/api/workspaces/invitations/mine")
}

/** Remove um convite respondido do histórico do usuário (soft delete). */
export async function deleteMyInvitation(invitationId: string): Promise<void> {
  await http.delete(`/api/workspaces/invitations/mine/${invitationId}`)
}

/** Recusa um convite de workspace (público) */
export async function rejectInvitation(token: string): Promise<void> {
  await http.post("/api/workspaces/invitations/reject", { token }, { public: true })
}

export async function getInvitationMetadata(token: string): Promise<InvitationMetadata> {
  return http.get<InvitationMetadata>(
    `/api/workspaces/invitations/${encodeURIComponent(token)}`,
    { public: true },
  )
}

// ─── Admin members ────────────────────────────────────────────────────────────

/** Lista todos os membros (ativos, pendentes, inativos) do workspace */
export async function getAdminMembers(workspaceId: string): Promise<WorkspaceMemberAdminDto[]> {
  const members = await http.get<CoreAdminMemberDto[]>(`/api/workspaces/${workspaceId}/members/admin`)
  return members.map(normalizeAdminMember)
}

/** Envia convites para novos membros */
export async function inviteMembers(workspaceId: string, payload: InviteMembersPayload): Promise<BulkInvitationResult[]> {
  return http.post<BulkInvitationResult[]>(`/api/workspaces/${workspaceId}/invitations/bulk`, {
    invitations: payload.invitations,
    userGroupIds: payload.userGroupIds ?? [],
    message: payload.message?.trim() || null,
  })
}

/** Baixa a relação administrativa de membros em CSV compatível com Excel. */
export async function exportAdminMembers(workspaceId: string): Promise<void> {
  const accessToken = await getAccessToken()
  const response = await fetch(`${API_BASE_URL}/api/workspaces/${workspaceId}/members/export`, {
    credentials: "include",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  })
  if (!response.ok) {
    throw new ApiError("Erro ao exportar membros.", response.status === 401 ? "UNAUTHORIZED" : "UNKNOWN", response.status)
  }

  const blob = await response.blob()
  const disposition = response.headers.get("content-disposition") ?? ""
  const fileNameMatch = disposition.match(/filename\*?=(?:UTF-8'')?[\"']?([^\"';]+)/i)
  const fileName = fileNameMatch?.[1]
    ? decodeURIComponent(fileNameMatch[1])
    : `membros-${new Date().toISOString().slice(0, 10)}.csv`
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = fileName
  anchor.click()
  URL.revokeObjectURL(url)
}

/** Altera a role de um membro (admin flow) */
export async function adminChangeMemberRole(
  workspaceId: string,
  memberId: string,
  payload: ChangeMemberRoleAdminPayload,
): Promise<void> {
  await http.put(`/api/workspaces/${workspaceId}/members/${memberId}/role`, {
    role: payload.roleName === "Operator" ? "Member" : payload.roleName,
  })

  if (payload.roleName === "Admin") {
    await http.put(`/api/workspaces/${workspaceId}/members/${memberId}/permissions`, {
      permissions: payload.modulePermissions.map((item) => ({
        module: item.module,
        actions: item.permissions.map((permission) => {
          if (permission === 1) return "Create"
          if (permission === 2) return "Alter"
          return "Remove"
        }),
      })),
    })
  }
}

/** Inativa um membro do workspace (admin flow) */
export async function adminInactivateMember(
  workspaceId: string,
  memberId: string,
  status?: string,
): Promise<void> {
  if (status === "Pending") {
    await http.delete(`/api/workspaces/${workspaceId}/invitations/${memberId}`)
    return
  }

  await http.delete(`/api/workspaces/${workspaceId}/members/${memberId}`)
}

// ─── Admin permissions ────────────────────────────────────────────────────────

/** Lista as permissões administrativas de um usuário no workspace */
export async function getAdminPermissionsForUser(
  workspaceId: string,
  userId: string,
): Promise<AdminPermissionResponse[]> {
  const members = await getAdminMembers(workspaceId)
  const member = members.find((item) => item.userId === userId && item.status === "Active")

  if (!member) {
    throw new ApiError("Membro nao encontrado no workspace.", "NOT_FOUND", 404)
  }

  const permissions = await http.get<CorePermissionDto[]>(
    `/api/workspaces/${workspaceId}/members/${member.memberId}/permissions`,
  )

  return permissions.flatMap(normalizePermission).map((permission) => ({
    ...permission,
    userId,
  }))
}
